import { ReloadOutlined } from '@ant-design/icons';
import type { BudgetQuotaItem } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount } from '@hq/utils';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Progress, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

const YEARS = Array.from({ length: 4 }, (_, index) => dayjs().year() - index);

/** 预算与定额：按科室/品类查看年度预算额度与已用额（来源 cfg_budget，编制下达后写入）。 */
export default function SystemBudgetQuota() {
  const [year, setYear] = useState(dayjs().year());

  const quota = useQuery({
    queryKey: ['system', 'budget-quota', year],
    queryFn: () => hq.opsExtras.budgetQuota.get({ budgetYear: year })
  });

  const header = quota.data?.header ?? null;
  const rows = quota.data?.items ?? [];
  const usedRate = header && header.totalAmount > 0 ? Math.min(100, Math.round((header.usedAmount / header.totalAmount) * 100)) : 0;

  const columns: ColumnsType<BudgetQuotaItem> = [
    { title: '科室', dataIndex: 'deptName', width: 180, render: (value: string | null, row) => value || row.deptId },
    { title: '品类', dataIndex: 'categoryId', width: 120, render: (value: string) => (value === '0' ? '不分品类' : value) },
    { title: '物资', dataIndex: 'materialId', width: 120, render: (value: string) => (value === '0' ? '不限' : value) },
    { title: '定额', dataIndex: 'amount', width: 140, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '已用', dataIndex: 'usedAmount', width: 140, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '剩余',
      key: 'remain',
      width: 140,
      align: 'right',
      render: (_, row) => {
        const remain = row.amount - row.usedAmount;
        return <Typography.Text type={remain < 0 ? 'danger' : undefined}>{formatAmount(remain)}</Typography.Text>;
      }
    },
    {
      title: '执行率',
      key: 'rate',
      width: 160,
      render: (_, row) => (
        <Progress
          size="small"
          percent={row.amount > 0 ? Math.min(100, Math.round((row.usedAmount / row.amount) * 100)) : 0}
          status={row.usedAmount > row.amount ? 'exception' : undefined}
        />
      )
    },
    { title: '备注', dataIndex: 'remark', ellipsis: true, render: (value: string | null) => value || '-' }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>预算与定额<Typography.Text type="secondary">按科室/品类分解的年度额度（编制下达后生效）</Typography.Text></Space>}
      extra={
        <Space>
          <Select value={year} options={YEARS.map((value) => ({ value, label: `${value} 年` }))} onChange={setYear} style={{ width: 120 }} />
          <Button icon={<ReloadOutlined />} loading={quota.isFetching} onClick={() => quota.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      {quota.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="预算定额加载失败"
          description={quota.error instanceof ApiError ? quota.error.friendlyMessage : String(quota.error)}
        />
      ) : null}
      {!header && !quota.isPending ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${year} 年尚无下达的预算`}
          description="预算编制（YS）审批通过并下达后，会在此生成机构总盘与科室/品类定额。"
        />
      ) : null}

      {header ? (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="预算总额" value={formatAmount(header.totalAmount)} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="已执行" value={formatAmount(header.usedAmount)} suffix={`/ ${usedRate}%`} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="预占" value={formatAmount(header.lockAmount)} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="预警阈值" value={`${Math.round(header.alertThreshold * 100)}%`} suffix={<Tag color="blue">{header.status}</Tag>} />
            </Card>
          </Col>
        </Row>
      ) : null}

      <Table<BudgetQuotaItem>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={quota.isPending}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无定额明细' }}
      />
    </Card>
  );
}
